import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Input,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Badge,
  Modal,
  Collapse,
  Pagination,
  Alert,
  Statistic
} from 'antd';
import {
  GlobalOutlined,
  ClockCircleOutlined,
  BellOutlined,
  EyeOutlined,
  PhoneOutlined,
  CalendarOutlined,
  PlusOutlined,
  FilterOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { Lead, User } from '../services/api';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import apiService from '../services/api';
import { getLeadCategoryStyle } from '../utils/leadCategoryColors';
import LeadDetailsModal from './LeadDetailsModal';
import EventModal from './EventModal';
import { useNavigate } from 'react-router-dom';

const { TabPane } = Tabs;
const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

interface CoberturaFidelizacaoProps {}

const CoberturaFidelizacao: React.FC<CoberturaFidelizacaoProps> = () => {
  // State for leads data
  const [semCoberturaLeads, setSemCoberturaLeads] = useState<Lead[]>([]);
  const [fidelizadosLeads, setFidelizadosLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  // State for filters
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedOperator, setSelectedOperator] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [searchText, setSearchText] = useState('');

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // State for modals
  const [novaCoberturaModalVisible, setNovaCoberturaModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [selectedLeadForEvent, setSelectedLeadForEvent] = useState<(Lead & { title?: string; activity_type?: string }) | null>(null);
  
  // Navigation
  const navigate = useNavigate();

  // Get user from auth context
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        setUser(null);
      }
    }
  }, []);

  // Fetch leads data
  useEffect(() => {
    if (user) {
      fetchLeads();
    }
  }, [user]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const allLeads = await apiService.getLeads();
      
      // Backend already handles role-based filtering correctly
      // Team Leaders can see their team members' leads, DC/Admin can see all leads
      // No additional frontend filtering needed
      let filteredLeads = allLeads;
      
      // Filter Sem Cobertura leads
      const semCobertura = filteredLeads.filter(lead => 
        lead.lead_status === 'Sem Cobertura'
      );
      setSemCoberturaLeads(semCobertura);

      // Filter Fidelizados leads
      const fidelizados = filteredLeads.filter(lead => 
        lead.lead_status === 'Fidelizado'
      );
      setFidelizadosLeads(fidelizados);
    } catch (error) {
      message.error('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  // Get unique cities from Sem Cobertura leads
  const getUniqueCities = () => {
    const cities = semCoberturaLeads
      .map(lead => lead.city)
      .filter(city => city && city.trim() !== '')
      .filter((city, index, arr) => arr.indexOf(city) === index);
    return cities.sort();
  };

  // Get unique operators from Sem Cobertura leads
  const getUniqueOperators = () => {
    const operators = semCoberturaLeads
      .map(lead => lead.sem_cobertura_de)
      .filter(operator => operator && operator.trim() !== '')
      .filter((operator, index, arr) => arr.indexOf(operator) === index);
    return operators.sort();
  };

  // Check if there are any leads to display
  const hasSemCoberturaLeads = semCoberturaLeads.length > 0;
  const hasFidelizadosLeads = fidelizadosLeads.length > 0;

  // Filter leads based on current filters
  const getFilteredSemCoberturaLeads = () => {
    let filtered = semCoberturaLeads;

    if (selectedCity) {
      filtered = filtered.filter(lead => lead.city === selectedCity);
    }

    if (selectedOperator) {
      filtered = filtered.filter(lead => lead.sem_cobertura_de === selectedOperator);
    }

    if (searchText) {
      filtered = filtered.filter(lead => 
        lead.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        lead.phone?.includes(searchText) ||
        lead.city?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  };

  // Get leads expiring soon (Fidelizados)
  const getExpiringLeads = (days: number) => {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + days);

    return fidelizadosLeads.filter(lead => {
      if (!lead.end_of_contract) return false;
      const contractEnd = new Date(lead.end_of_contract);
      return contractEnd <= futureDate && contractEnd >= now;
    });
  };

  // Columns for Sem Cobertura table
  const getSemCoberturaColumns = () => [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Lead) => (
        <div>
          <div style={{ fontWeight: 500, ...getLeadCategoryStyle(record.lead_category) }}>
            {name}
            {/* Show user icon for team leaders' own leads */}
            {user && (user.role === 'team_leader' || user.role === 'admin' || user.role === 'super_admin') && 
             record.assigned_to === user.id && (
              <span style={{ marginLeft: '8px', fontSize: '14px' }}>👤</span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            NIF: {record.dif || '-'}
          </div>
        </div>
      ),
      sorter: (a: Lead, b: Lead) => a.name.localeCompare(b.name)
    },
    {
      title: 'Contacto',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-'
    },
    {
      title: 'Cidade',
      dataIndex: 'city',
      key: 'city',
      render: (city: string) => city || '-',
      sorter: (a: Lead, b: Lead) => (a.city || '').localeCompare(b.city || '')
    },
    {
      title: 'Morada',
      dataIndex: 'address',
      key: 'address',
      render: (address: string) => address || '-'
    },
    {
      title: 'Sem Cobertura de',
      dataIndex: 'sem_cobertura_de',
      key: 'sem_cobertura_de',
      render: (operator: string) => (
        <Tag color="red">{operator || '-'}</Tag>
      )
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (text: string, record: Lead) => (
        <Space>
          <Tooltip title="Ver detalhes">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewLead(record)}
            />
          </Tooltip>
                     <Tooltip title="Criar Evento de Contacto">
             <Button 
               type="primary" 
               icon={<CalendarOutlined />} 
               size="small"
               onClick={() => handleCreateContactEvent(record)}
             />
           </Tooltip>
        </Space>
      )
    }
  ];

  // Columns for Fidelizados table
  const getFidelizadosColumns = () => [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Lead) => (
        <div>
          <div style={{ fontWeight: 500, ...getLeadCategoryStyle(record.lead_category) }}>
            {name}
            {/* Show user icon for team leaders' own leads */}
            {user && (user.role === 'team_leader' || user.role === 'admin' || user.role === 'super_admin') && 
             record.assigned_to === user.id && (
              <span style={{ marginLeft: '8px', fontSize: '14px' }}>👤</span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            NIF: {record.dif || '-'}
          </div>
        </div>
      ),
      sorter: (a: Lead, b: Lead) => a.name.localeCompare(b.name)
    },
    {
      title: 'Contacto',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-'
    },
    {
      title: 'Fim da Fidelização',
      dataIndex: 'end_of_contract',
      key: 'end_of_contract',
      render: (date: string, record: Lead) => {
        if (!date) return '-';
        
        const contractEnd = new Date(date);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((contractEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        let color = 'green';
        if (daysUntilExpiry <= 30) color = 'red';
        else if (daysUntilExpiry <= 60) color = 'orange';
        
        return (
          <div>
            <div>{new Date(date).toLocaleDateString('pt-PT')}</div>
            <Tag color={color}>
              {daysUntilExpiry > 0 ? `${daysUntilExpiry} dias` : 'Expirado'}
            </Tag>
          </div>
        );
      },
      sorter: (a: Lead, b: Lead) => {
        const dateA = a.end_of_contract ? new Date(a.end_of_contract).getTime() : 0;
        const dateB = b.end_of_contract ? new Date(b.end_of_contract).getTime() : 0;
        return dateA - dateB;
      }
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (text: string, record: Lead) => (
        <Space>
          <Tooltip title="Ver detalhes">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={() => handleViewLead(record)}
            />
          </Tooltip>
                     <Tooltip title="Criar Evento de Contacto">
             <Button 
               type="primary" 
               icon={<CalendarOutlined />} 
               size="small"
               onClick={() => handleCreateContactEvent(record)}
             />
           </Tooltip>
        </Space>
      )
    }
  ];

  // Handler functions
  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setViewModalVisible(true);
  };

  const handleCreateContactEvent = (lead: Lead) => {
    // Create event data with pre-filled contact information
    const eventData = {
      ...lead,
      title: `Contacto: ${lead.name}`,
      activity_type: 'contact'
    } as Lead & { title: string; activity_type: string };
    
    setSelectedLeadForEvent(eventData);
    setEventModalVisible(true);
  };

  const handleCloseModal = () => {
    setViewModalVisible(false);
    setSelectedLead(null);
  };

  const handleEventModalClose = () => {
    setEventModalVisible(false);
    setSelectedLeadForEvent(null);
  };

  const handleEventModalSuccess = () => {
    // Optionally refresh leads or show success message
    message.success('Evento criado com sucesso!');
  };



  const handleNovaCobertura = () => {
    setNovaCoberturaModalVisible(true);
  };



  const handleClearFilters = () => {
    setSelectedCity('');
    setSelectedOperator('');
    setDateRange(null);
    setSearchText('');
  };

  // Statistics
  const totalSemCobertura = semCoberturaLeads.length;
  const totalFidelizados = fidelizadosLeads.length;
  const expiring30Days = getExpiringLeads(30).length;
  const expiring60Days = getExpiringLeads(60).length;
  
  // Check if user can see all leads or only their own
  const canSeeAllLeads = user && (user.role === 'DC' || user.role === 'Admin');
  const dataScopeText = canSeeAllLeads ? 'Todos os leads' : 'Meus leads';

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={2}>Cobertura e Fidelização</Title>
        <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
          Visualizando: {dataScopeText}
        </div>
      </div>
      
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Sem Cobertura"
              value={totalSemCobertura}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Fidelizados"
              value={totalFidelizados}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Expira <30 dias"
              value={expiring30Days}
              prefix={<BellOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Expira <60 dias"
              value={expiring60Days}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card>
                         <Space>
               <Button 
                 type="primary" 
                 icon={<PlusOutlined />}
                 onClick={handleNovaCobertura}
               >
                 Nova Cobertura
               </Button>
             </Space>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card>
        <Tabs defaultActiveKey="semCobertura">
          <TabPane 
            tab={
              <span>
                <GlobalOutlined />
                Sem Cobertura ({totalSemCobertura})
              </span>
            } 
            key="semCobertura"
          >
            {/* Filters */}
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={6}>
                <Select
                  placeholder="Filtrar por cidade"
                  value={selectedCity}
                  onChange={setSelectedCity}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {getUniqueCities().map(city => (
                    <Option key={city} value={city}>{city}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={6}>
                <Select
                  placeholder="Filtrar por operador"
                  value={selectedOperator}
                  onChange={setSelectedOperator}
                  allowClear
                  style={{ width: '100%' }}
                >
                  {getUniqueOperators().map(operator => (
                    <Option key={operator} value={operator}>{operator}</Option>
                  ))}
                </Select>
              </Col>
              <Col span={8}>
                <Search
                  placeholder="Pesquisar por nome, telefone ou cidade"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              </Col>
              <Col span={4}>
                <Button 
                  icon={<FilterOutlined />}
                  onClick={handleClearFilters}
                >
                  Limpar Filtros
                </Button>
              </Col>
            </Row>

            {/* Table */}
            <Table
              columns={getSemCoberturaColumns()}
              dataSource={getFilteredSemCoberturaLeads()}
              loading={loading}
              rowKey="id"
              locale={{
                emptyText: hasSemCoberturaLeads 
                   ? 'Nenhum lead encontrado com os filtros aplicados' 
                   : canSeeAllLeads 
                     ? 'Nenhum lead sem cobertura encontrado' 
                     : 'Nenhum lead sem cobertura encontrado nos seus leads'
               }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: getFilteredSemCoberturaLeads().length,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size || 10);
                },
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} de ${total} registos`
              }}
            />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <ClockCircleOutlined />
                Fidelizados ({totalFidelizados})
              </span>
            } 
            key="fidelizados"
          >
            {/* Alerts for expiring contracts */}
            {expiring30Days > 0 && (
              <Alert
                message={`${expiring30Days} contratos expiram em menos de 30 dias!`}
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}
            {expiring60Days > 0 && expiring30Days === 0 && (
              <Alert
                message={`${expiring60Days} contratos expiram em menos de 60 dias`}
                type="warning"
                showIcon
                style={{ marginBottom: '16px' }}
              />
            )}

            {/* Table */}
            <Table
              columns={getFidelizadosColumns()}
              dataSource={fidelizadosLeads}
              loading={loading}
              rowKey="id"
              locale={{
                emptyText: hasFidelizadosLeads 
                   ? 'Nenhum lead encontrado com os filtros aplicados' 
                   : canSeeAllLeads 
                     ? 'Nenhum lead fidelizado encontrado' 
                     : 'Nenhum lead fidelizado encontrado nos seus leads'
               }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: fidelizadosLeads.length,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size || 10);
                },
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} de ${total} registos`
              }}
            />
          </TabPane>

          
        </Tabs>
      </Card>

             {/* Nova Cobertura Modal */}
       <Modal
         title="Adicionar Nova Cobertura"
         open={novaCoberturaModalVisible}
         onCancel={() => setNovaCoberturaModalVisible(false)}
         footer={[
           <Button key="cancel" onClick={() => setNovaCoberturaModalVisible(false)}>
             Cancelar
           </Button>,
           <Button key="submit" type="primary" onClick={() => {
             message.success('Nova cobertura adicionada!');
             setNovaCoberturaModalVisible(false);
           }}>
             Adicionar
           </Button>
         ]}
       >
         <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
           <Text type="secondary">Formulário para adicionar nova cobertura será implementado aqui</Text>
         </div>
       </Modal>

               {/* Lead Details Modal */}
        <LeadDetailsModal 
          open={viewModalVisible} 
          lead={selectedLead} 
          onClose={handleCloseModal} 
        />

        {/* Event Modal */}
        <EventModal 
          open={eventModalVisible}
          preSelectedLead={selectedLeadForEvent}
          onClose={handleEventModalClose}
          onSuccess={handleEventModalSuccess}
        />
      </div>
    );
  };

export default CoberturaFidelizacao;
